import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CONNECTABLE_PLATFORMS, PLATFORM_LABELS } from '@affily/shared/constants';
import { formatDay } from '@affily/shared/programDates';
import { formatCount } from '@affily/shared/money';
import { api } from '../lib/api.js';
import { useFetch } from '../hooks/useFetch.js';
import { Button, Alert, Badge, Spinner } from './ui.jsx';

/** The server's code for "connect an account before applying". */
export const SOCIAL_REQUIRED = 'SOCIAL_REQUIRED';
export const isSocialRequiredError = (err) => err?.details?.code === SOCIAL_REQUIRED;

/** The creator's connected accounts, and which platforms this Affily can connect. */
export const useSocialAccounts = ({ skip = false } = {}) => useFetch('/connections/social', { skip });

/** Whether applying would be refused for want of an account (never when nothing can be connected). */
export function needsSocial(data) {
  if (!data) return false;
  const canConnect = Object.values(data.available ?? {}).some(Boolean);
  return canConnect && (data.accounts ?? []).length === 0;
}

const PLATFORM_HINTS = {
  tiktok: 'One click on TikTok to approve.',
  instagram:
    'A business or creator account — Instagram shares numbers for those only. When Instagram asks, leave “Access and manage insights” on: it is how Affily reads your views.',
  youtube:
    'Sign in with the Google account your channel is on, and tick every box (or “Select all”) when Google asks — each one is a number Affily reads.',
  facebook:
    'A Facebook Page — Facebook shares follower counts for Pages only. When Facebook asks which Pages, tick just the one you post on; tick several and Affily asks which is yours.',
};

/** How an account is named: TikTok and Instagram by @handle, YouTube by its own @handle, a Page by its name. */
export function accountName(account) {
  if (!account.username) return account.displayName ?? PLATFORM_LABELS[account.platform];
  if (account.platform === 'tiktok' || account.platform === 'instagram') return `@${account.username}`;
  return account.username;
}

/**
 * One-click connections to a creator's social accounts, one card per platform.
 *
 * Connecting reads the account's followers onto the profile as verified, and
 * is what applying to a program needs. The platform sends the creator back to
 * `next` (this page by default), where the outcome is shown once.
 *
 * Pass `social` when the page already holds the accounts (useSocialAccounts),
 * so both stay in step; otherwise it loads them itself.
 */
export function SocialConnect({ social: given, next, platforms = CONNECTABLE_PLATFORMS, onChanged }) {
  const own = useSocialAccounts({ skip: Boolean(given) });
  const social = given ?? own;
  const [params, setParams] = useSearchParams();
  const [error, setError] = useState('');

  const accounts = social.data?.accounts ?? [];
  const available = social.data?.available ?? {};

  const connectedPlatform = params.get('connected');
  // The reason comes from Affily itself, never from the address bar.
  const callbackError =
    params.get('failed') === '1'
      ? (social.data?.connectError ??
        `Connecting ${PLATFORM_LABELS[connectedPlatform] ?? 'that account'} didn’t work. Try again.`)
      : null;

  function dismiss() {
    const rest = new URLSearchParams(params);
    rest.delete('connected');
    rest.delete('failed');
    setParams(rest, { replace: true });
  }

  async function connect(platform) {
    setError('');
    try {
      const { redirectUrl } = await api.post(`/connections/social/${platform}/start`, {
        next: next ?? window.location.pathname,
      });
      window.location.assign(redirectUrl);
    } catch (err) {
      setError(err.message);
    }
  }

  async function choose(account, id) {
    setError('');
    try {
      await api.post(`/connections/social/${account.id}/choose`, { id });
      await social.reload();
      onChanged?.();
    } catch (err) {
      setError(err.message);
    }
  }

  async function disconnect(account) {
    setError('');
    try {
      await api.delete(`/connections/social/${account.id}`);
      await social.reload();
      onChanged?.();
    } catch (err) {
      setError(err.message);
    }
  }

  if (social.loading) return <Spinner />;

  return (
    <div className="space-y-3">
      {connectedPlatform && (
        <Alert tone={callbackError ? 'negative' : 'positive'}>
          {callbackError ?? `${PLATFORM_LABELS[connectedPlatform] ?? 'Account'} connected.`}{' '}
          <button type="button" className="underline" onClick={dismiss}>
            Dismiss
          </button>
        </Alert>
      )}
      <Alert>{error}</Alert>

      <div className="grid gap-3 sm:grid-cols-2">
        {platforms.map((platform) => {
          const connected = accounts.filter((a) => a.platform === platform);
          return (
            <div key={platform} className="rounded-lg border border-rule bg-paper-raised p-4">
              <p className="font-medium">{PLATFORM_LABELS[platform]}</p>
              {connected.length > 0 ? (
                connected.map((account) => (
                  <div key={account.id} className="mt-1">
                    <p className="text-sm text-ink-muted">
                      {accountName(account)}
                      {account.status !== 'active' && (
                        <Badge tone="negative" className="ml-2">
                          needs reconnecting
                        </Badge>
                      )}
                    </p>
                    {account.status === 'active' && account.lastStatsAt && (
                      <p className="mt-0.5 text-xs text-positive">
                        Followers verified · read {formatDay(account.lastStatsAt)}
                      </p>
                    )}
                    {account.problem && <p className="mt-1 text-xs text-negative">{account.problem}</p>}
                    {account.choices?.length > 1 && (
                      <PagePicker account={account} onChoose={(id) => choose(account, id)} />
                    )}
                    {account.missingPermissions?.length > 0 && (
                      <p className="mt-1 text-xs text-negative">
                        You left out{' '}
                        {account.missingPermissions.map((p) => `“${p.label}”`).join(' and ')}, so Affily can’t read{' '}
                        {account.missingPermissions.map((p) => p.loses).join(' or ')}. Reconnect and tick every box.
                      </p>
                    )}
                    <div className="mt-3 flex gap-2">
                      {(account.status !== 'active' || account.missingPermissions?.length > 0) && (
                        <Button size="sm" onClick={() => connect(platform)}>
                          Reconnect
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => disconnect(account)}>
                        Disconnect
                      </Button>
                    </div>
                  </div>
                ))
              ) : available[platform] ? (
                <>
                  <p className="mt-1 text-sm text-ink-muted">{PLATFORM_HINTS[platform]}</p>
                  <Button size="sm" className="mt-3" onClick={() => connect(platform)}>
                    Connect {PLATFORM_LABELS[platform]}
                  </Button>
                </>
              ) : (
                <p className="mt-1 text-sm text-ink-faint">Not available on Affily yet.</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Several Pages were granted: which one is the creator's. Until they say, the
 * one with the most followers stands in.
 */
function PagePicker({ account, onChoose }) {
  const [picked, setPicked] = useState(account.choices[0].id);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    try {
      await onChoose(picked);
    } finally {
      setBusy(false);
    }
  }

  return (
    <fieldset className="mt-3 rounded-md border border-accent/40 bg-accent-soft/40 p-3">
      <legend className="px-1 text-sm font-medium">Which Page is yours?</legend>
      <p className="text-xs text-ink-muted">
        You gave Affily {account.choices.length} Pages. Pick the one you post on; Affily drops access to the others.
      </p>
      <div className="mt-2 space-y-1">
        {account.choices.map((page) => (
          <label key={page.id} className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="radio"
              name={`page-${account.id}`}
              value={page.id}
              checked={picked === page.id}
              onChange={() => setPicked(page.id)}
            />
            <span>{page.name ?? page.id}</span>
            {page.followers != null && (
              <span className="text-xs text-ink-faint">{formatCount(page.followers)} followers</span>
            )}
          </label>
        ))}
      </div>
      <Button size="sm" className="mt-3" disabled={busy} onClick={submit}>
        {busy ? 'Saving…' : 'Use this Page'}
      </Button>
    </fieldset>
  );
}
